import {
    BooleanField,
    Datagrid,
    TextField,
    ListBase,
    Title,
    CreateButton,
    ListToolbar,
    TopToolbar,
    FunctionField,
    ListContextProvider,
    useList,
    useListContext,
} from 'react-admin';
import type { MissionTypeId, MissionType } from '@wwf/sdk';
import { useMissionTypeDrawer } from '../drawer/MissionTypeDrawer';
import { MissionTypeIconCell } from './MissionTypeIconCell';
import { Stack, Card } from '@mui/material';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { findMemberTypeLabel } from '@app/member/findMemberTypeLabel';

export const MissionTypeList = () => {
    return (
        <ListBase resource="missionType">
            <MissionTypeListView />
        </ListBase>
    );
};

const MissionTypeListView = () => {
    const { openDrawer } = useMissionTypeDrawer();
    const { memberTypeLabels } = useConfigurationContext();
    const { data, isPending } = useListContext<MissionType>();
    const localListContext = useList({
        data,
        isPending,
        resource: 'missionType',
        sort: { field: 'name', order: 'ASC' },
        perPage: 1000,
    });

    return (
        <Stack sx={{ width: '100%' }}>
            <Title title="Mission types" />
            <ListToolbar
                actions={
                    <MissionTypeListActions
                        openCreateDrawer={() => openDrawer('create', null)}
                    />
                }
            />

            <Card>
                <ListContextProvider value={localListContext}>
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick={key => {
                            openDrawer('show', key as MissionTypeId);
                            return false;
                        }}
                    >
                        <TextField source="id" label="Id" />
                        <BooleanField source="enabled" />
                        <FunctionField<MissionType>
                            label="Icon"
                            render={record => (
                                <MissionTypeIconCell iconId={record.icon?.id} />
                            )}
                        />
                        <TextField source="name" />
                        <TextField
                            source="shortDescription"
                            label="Description"
                        />
                        <FunctionField<MissionType>
                            label="Who can see"
                            render={record =>
                                record.whoCanSee
                                    ? findMemberTypeLabel(
                                          memberTypeLabels,
                                          record.whoCanSee
                                      )
                                    : null
                            }
                        />
                        <FunctionField<MissionType>
                            label="Who can participate"
                            render={record =>
                                record.whoCanParticipate
                                    ? findMemberTypeLabel(
                                          memberTypeLabels,
                                          record.whoCanParticipate
                                      )
                                    : null
                            }
                        />
                        <FunctionField<MissionType>
                            label="Who can create"
                            render={record =>
                                record.whoCanCreate
                                    ? findMemberTypeLabel(
                                          memberTypeLabels,
                                          record.whoCanCreate
                                      )
                                    : null
                            }
                        />
                    </Datagrid>
                </ListContextProvider>
            </Card>
        </Stack>
    );
};

const MissionTypeListActions = ({
    openCreateDrawer,
}: {
    openCreateDrawer: () => void;
}) => (
    <TopToolbar>
        <CreateButton
            variant="contained"
            label="New mission type"
            onClick={openCreateDrawer}
            sx={{ marginLeft: 2 }}
        />
    </TopToolbar>
);
